import {Box, Button, Grid, Paper, useTheme} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useFormContext} from "react-hook-form";
import {IExpiredPracticeActionDatesParams, IExpiredPracticeDetailForm} from "../../models/models";
import {FormCheckbox, FormDatePicker} from "../../common/form-fields-wrappers";
import { useContext, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { updateActionDates } from "../../services/http.services";
import { useSetRecoilState } from "recoil";
import { practiceDetail } from "../../state";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../../util/state/globalVariables";
import CustomLoader from "../../../../util/component/CustomLoader";
import dayjs from "dayjs";
import { setSnackbar } from "../../../../util/state/globalAction";

const ExpiredThresholds = () => {
    const theme = useTheme();
    const {t} = useTranslation(['expiredManagement']);
    const {dispatchGlobalData} = useContext(GlobalContext);

    const expiredPracticeDetailForm = useFormContext<IExpiredPracticeDetailForm>();

    const setPracticeDetail = useSetRecoilState(practiceDetail);
    
    const updateActionDatesMutation = useMutation({
        mutationFn: (params: IExpiredPracticeActionDatesParams) => {
      
            return updateActionDates(params);
        },
        onSuccess(response: any) {

            setPracticeDetail(response.data);

            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('common.message.success'),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true,
                    }
                }
            });
        },
        onError(error: any) {

            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t(`common.message.error.updateActionDates.${error?.response?.status}`, t('common.message.error.tryLater')),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });
        },
    });

    const onConfirm = (formData: IExpiredPracticeDetailForm) => {
        const {firstSmsExpectedDate, recoverySocietyExpectedDate, formalNoticeExpectedDate, lawSuitExpectedDate, autoSms} = expiredPracticeDetailForm.getValues("thresholds");

        const params: IExpiredPracticeActionDatesParams = {
            id: formData.detail.expiredCode,
            actionDates: {
                firstSmsExpectedDate: dayjs.isDayjs(firstSmsExpectedDate) ? firstSmsExpectedDate.toISOString() : '',
                recoverySocietyExpectedDate: dayjs.isDayjs(recoverySocietyExpectedDate) ? recoverySocietyExpectedDate.toISOString() : '',
                formalNoticeExpectedDate: dayjs.isDayjs(formalNoticeExpectedDate) ? formalNoticeExpectedDate?.toISOString() : '',
                lawSuitExpectedDate: dayjs.isDayjs(lawSuitExpectedDate) ? lawSuitExpectedDate?.toISOString() : '',
                autoSms: autoSms
            }
        };

        updateActionDatesMutation.mutate(params);
    }

    useEffect(() => {
        expiredPracticeDetailForm.clearErrors("thresholds");
    }, []);

    return (
        <>
            <Paper elevation={1} sx={{p: theme.spacing(2), mb: theme.spacing(2)}}>
                <Grid container columnSpacing={2} sx={{mb: theme.spacing(2)}}>
                    <Grid item xs={12} sm={6} lg={3} sx={{display: "flex", alignItems: "center", my: theme.spacing(1)}}>
                        <FormDatePicker<IExpiredPracticeDetailForm>
                            name="thresholds.firstSmsExpectedDate"
                            control={expiredPracticeDetailForm.control}
                            label={t('expiredDetail.form.actionDates.firstSmsExpectedDate')}
                        />
                    </Grid>
                    <Grid item sm={6} lg={9} />

                    <Grid item xs={12} sm={6} lg={3} sx={{display: "flex", alignItems: "center", my: theme.spacing(1)}}>
                        <FormDatePicker<IExpiredPracticeDetailForm>
                            name="thresholds.formalNoticeExpectedDate"
                            control={expiredPracticeDetailForm.control}
                            label={t('expiredDetail.form.actionDates.formalNoticeExpectedDate')}
                        />
                    </Grid>
                    <Grid item sm={6} lg={9} />
                    
                    <Grid item xs={12} sm={6} lg={3} sx={{display: "flex", alignItems: "center", my: theme.spacing(1)}}>
                        <FormDatePicker<IExpiredPracticeDetailForm>
                            name="thresholds.recoverySocietyExpectedDate"
                            control={expiredPracticeDetailForm.control}
                            label={t('expiredDetail.form.actionDates.recoverySocietyExpectedDate')}
                        />
                    </Grid>
                    <Grid item sm={6} lg={9} />

                    <Grid item xs={12} sm={6} lg={3} sx={{display: "flex", alignItems: "center", my: theme.spacing(1)}}>
                        <FormDatePicker<IExpiredPracticeDetailForm>
                            name="thresholds.lawSuitExpectedDate"
                            control={expiredPracticeDetailForm.control}
                            label={t('expiredDetail.form.actionDates.lawSuitExpectedDate')}
                        />
                    </Grid>

                    <Grid item sm={6} lg={9} />

                    <Grid item xs={12} sm={6} lg={3} sx={{display: "flex", alignItems: "center", my: theme.spacing(1)}}>
                        <FormCheckbox<IExpiredPracticeDetailForm>
                            name="thresholds.autoSms" 
                            control={expiredPracticeDetailForm.control}
                            label={t('expiredDetail.form.actionDates.autoSms')}
                        />
                    </Grid>

                    <Grid item sm={6} lg={9} />
                </Grid>

                <Box sx={{py: theme.spacing(2), display: "flex", justifyContent: "flex-end"}}>
                    <Button onClick={expiredPracticeDetailForm.handleSubmit(onConfirm)}>
                        {t('common.button.save')}
                    </Button>
                </Box>
            </Paper>
            <CustomLoader loading={updateActionDatesMutation.isLoading} />
        </>
    )
}

export default ExpiredThresholds;